/**
 * SDK 源站解析（接口 + 静态资源）。
 *
 * 关键点：SDK 会被嵌入到**第三方网站**，绝不能回退到 location.origin —— 那会把
 * /api、/imlogo 请求打到宿主网站导致 404。解析优先级：
 *   1) init() / 页面设定的 window.__oc_apiBase__（取其 origin）
 *   2) 从 SDK 自身的 <script src> 推断 origin —— 自动适配任意部署域名（域名变更无需改码）
 *   3) 构建期由 .env 注入的 VITE_SDK_ORIGIN，最终兜底 https://16x.ai
 */
const FALLBACK_ORIGIN: string =
  ((import.meta as any).env && (import.meta as any).env.VITE_SDK_ORIGIN) || "https://16x.ai";

/** 返回 SDK 源站 origin，例如 https://16x.ai（不含路径）。 */
export function resolveSdkOrigin(): string {
  if (typeof window !== "undefined" && (window as any).__oc_apiBase__) {
    try { return new URL((window as any).__oc_apiBase__).origin; } catch {}
  }
  if (typeof document !== "undefined") {
    try {
      const scripts = document.getElementsByTagName("script");
      for (let i = scripts.length - 1; i >= 0; i--) {
        const src = scripts[i].src;
        if (src && (src.indexOf("/onecall-sdk") !== -1 || src.indexOf("/sdk/dist") !== -1)) {
          return new URL(src).origin;
        }
      }
    } catch {}
  }
  return FALLBACK_ORIGIN;
}

/** 返回 API 基地址，例如 https://16x.ai/api。 */
export function resolveApiBase(): string {
  if (typeof window !== "undefined" && (window as any).__oc_apiBase__) {
    return (window as any).__oc_apiBase__;
  }
  return resolveSdkOrigin() + "/api";
}

/** 把站内绝对路径（如 /imlogo/wechat.svg）拼成 SDK 源站的完整 URL。 */
export function assetUrl(path: string): string {
  return resolveSdkOrigin() + (path.charAt(0) === "/" ? path : "/" + path);
}

/**
 * 归一化后端返回的媒体地址（buttonLogo / aiAvatar / 上传图片等）。
 * 已是完整 URL / data: / 协议相对 的原样返回；站内相对路径（如 /uploads/...）补上 SDK 源站。
 * 跨站嵌入时这些相对路径若不补域名会打到宿主站 → 404。
 */
export function mediaUrl(v: string | null | undefined): string {
  if (!v) return "";
  if (/^(https?:)?\/\//i.test(v) || v.indexOf("data:") === 0 || v.indexOf("blob:") === 0) return v;
  return assetUrl(v);
}
